import { Fragment, useState } from 'react';
import { FieldArray, FormikProvider, getIn, useFormik } from 'formik';
import * as Yup from 'yup';
import { Check, Copy, Info, Mail, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useEmailSeatCredentials, useIssueLicense } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { cn } from '@/shared/lib/utils';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import type { CustomerDetailResponse, IssueLicenseResponse } from '@/types';
import { credentialsText, roleLabel, ROLES, type SeatRole } from './credentials';

/**
 * A second (or third) licence for an organization that already exists.
 *
 * Two steps, not three: there is no organization step here at all, since one
 * already exists - this is exactly the licence half of what used to be the
 * new-organization wizard, now reachable on its own from the org it belongs
 * to. `license_seats.email` is NOT NULL and UNIQUE, so - unlike the design
 * mockup this screen is modelled on - a seat still needs a real person named
 * up front; there is no way to buy "4 seats" with nobody attached yet.
 */

/**
 * Named and described as the design does, not by seat arithmetic.
 *
 * "1 seat" / "Up to 5 seats" was the old subtitle, and it only repeated the
 * count already shown on the People header below. What actually differs
 * between the two plans - and what an admin picking one needs to know - is how
 * the licence binds to hardware and who is allowed to unbind it.
 */
const PLANS = [
  { value: 'single_seat', label: 'Single Seat', hint: 'One fingerprint. Resets require an admin.' },
  {
    value: 'lab_seat',
    label: 'Lab Seat',
    hint: 'Seats bind per workstation. Owner manages the roster.',
  },
] as const;

type Plan = (typeof PLANS)[number]['value'];

/** Derived from the contract rather than re-declared, so a new org type here
 *  is a compile error rather than a silently unhandled case. */
type OrgType = CustomerDetailResponse['orgType'];

/**
 * Seats are capacity, not a headcount.
 *
 * `licenses.seat_count` is what the customer bought; a `license_seats` row
 * appears only once somebody is named on it, because `license_seats.email` is
 * NOT NULL and UNIQUE. So a lab buys forty seats, hands over one address on
 * the day, and the remaining thirty-nine are claimed later from the
 * organization's own page.
 *
 * Single Seat is exactly one - that is the entire product difference between
 * the two plans. Lab Seat has no ceiling beyond the server's sanity bound.
 */
const SINGLE_SEAT_COUNT = 1;
const MAX_SEAT_COUNT = 10_000;
const maxSeatsFor = (plan: Plan) => (plan === 'single_seat' ? SINGLE_SEAT_COUNT : MAX_SEAT_COUNT);

/*
 * There is no password field, deliberately.
 *
 * The server generates one per seat and returns it exactly once, on the
 * results screen. Offering to type one instead only invites a password chosen
 * to be easy to read down the phone, reused across every seat in the lab, and
 * never changed - and the admin typing it is not the person who will use it.
 * `POST /customers/:id/licenses` treats an absent password as "generate one",
 * so leaving the field out is the whole change.
 */

/** Mirrored from prisma/seed.ts's POLICIES - see ProvisionWizard's note on why
 *  this is kept in sync by hand rather than fetched. */
const POLICY_INFO: Record<Plan, { fingerprint: string; grace: string }> = {
  single_seat: { fingerprint: 'Soft grace on a hardware change', grace: '14 days' },
  lab_seat: { fingerprint: 'Blocks an unrecognised machine immediately', grace: '7 days' },
};

/**
 * A sanity bound, not a product rule - same reasoning as `MAX_SEAT_COUNT`.
 * Kept just under the server's own 3650-day (~119.9-month) ceiling on
 * `validityDays` so a term typed at the limit here is never the one rejected
 * for exceeding it there.
 */
const MAX_TERM_MONTHS = 119;

/** A term's day-count, from the moment the seat is bought - every seat's
 *  clock starts at issue now, not whenever a device happens to activate it,
 *  which is what keeps every seat on one lab licence expiring together. */
const validityDaysFor = (months: number): number => Math.round(months * 30.44);

interface SeatDraft {
  email: string;
  fullName: string;
  role: SeatRole;
}

interface FormValues {
  plan: Plan;
  /** Seats bought. `seats.length` is how many of them are named right now. */
  seatCount: number;
  /** Whatever whole number of months the operator typed. */
  termMonths: number;
  salesDealReference: string;
  seats: SeatDraft[];
}

const emptySeat = (role: SeatRole = 'technician'): SeatDraft => ({
  email: '',
  fullName: '',
  role,
});

/**
 * Built per-organization rather than declared as a constant, because the
 * plan depends on the organization's own type.
 *
 * Every licence gets its own owner, independent of any other licence the
 * organization already holds - ownership is per-licence, not per-organization
 * - so the first seat always opens on `owner`, unconditionally.
 *
 * Computed here, at first render, rather than pushed in by an effect after
 * mount: an effect that calls setFieldValue runs twice under StrictMode and
 * can land after the user has already touched the field.
 */
const buildInitialValues = (orgType: OrgType): FormValues => {
  // An individual dentist can only hold a Single Seat licence - the org type
  // is the claim that they are one person at one workstation - so that is
  // what the form opens on rather than a plan they cannot choose.
  const soleTrader = orgType === 'individual_dentist';

  return {
    plan: soleTrader ? 'single_seat' : 'lab_seat',
    seatCount: 1,
    termMonths: 12,
    salesDealReference: '',
    seats: [emptySeat('owner')],
  };
};

/**
 * This licence needs exactly one owner among its own initial seats - there is
 * no later screen that assigns one (see `AddSeatDialog`, which no longer
 * offers the role at all), and it is independent of whatever the
 * organization's other licences already have.
 */
const schema = Yup.object({
  plan: Yup.string().oneOf(['single_seat', 'lab_seat']).required(),
  seatCount: Yup.number()
    .integer()
    .min(1, 'At least one seat.')
    .max(MAX_SEAT_COUNT, `At most ${String(MAX_SEAT_COUNT)} seats.`)
    .required(),
  termMonths: Yup.number()
    .integer('Whole months only.')
    .min(1, 'At least 1 month.')
    .max(MAX_TERM_MONTHS, `At most ${String(MAX_TERM_MONTHS)} months.`)
    .required('Enter a term in months.'),
  salesDealReference: Yup.string().trim().max(120),
  seats: Yup.array()
    .of(
      Yup.object({
        fullName: Yup.string().trim().required('Enter their name.').max(200),
        email: Yup.string()
          .trim()
          .required('Enter their email address.')
          .email('Enter a valid email address.')
          .max(320),
        role: Yup.string().oneOf(['owner', 'technician', 'viewer']).required(),
      }),
    )
    .min(1, 'Name at least one person - a licence nobody can use is not worth issuing.')
    .test('within-capacity', 'More people than this licence has seats.', function (seats) {
      const { seatCount } = this.parent as FormValues;
      return (seats?.length ?? 0) <= seatCount;
    })
    .test('unique-emails', 'The same email appears twice.', (seats) => {
      const emails = (seats ?? [])
        .map((seat) => seat.email?.trim().toLowerCase())
        .filter((email): email is string => Boolean(email));
      return new Set(emails).size === emails.length;
    })
    // Nothing in the schema stops two owners - `portal_users.role` is a
    // plain enum - so the only place this can be caught is before it is
    // written. The server enforces the same rule (`assertOwnerAssignment`);
    // this is just the fast, client-side version of it.
    .test('one-owner', 'Choose exactly one person to be the owner.', (seats) => {
      return (seats ?? []).filter((seat) => seat.role === 'owner').length === 1;
    }),
});

type Step = 1 | 2;

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Licence' },
  { n: 2, label: 'Confirm & issue' },
];

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex items-center" aria-hidden="true">
      {STEPS.map((s, index) => (
        <Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                step === s.n
                  ? 'bg-primary text-primary-foreground'
                  : step > s.n
                    ? 'bg-primary/15 text-primary'
                    : 'border border-input text-muted-foreground',
              )}
            >
              {step > s.n ? <Check className="size-3.5" /> : s.n}
            </span>
            <span
              className={cn(
                'text-sm font-medium whitespace-nowrap',
                step === s.n ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {s.label}
            </span>
          </div>
          {index < STEPS.length - 1 && <div className="mx-3 h-px min-w-6 flex-1 bg-border" />}
        </Fragment>
      ))}
    </div>
  );
}

function PlanCard({
  title,
  subtitle,
  selected,
  disabled = false,
  disabledReason,
  onSelect,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  disabled?: boolean;
  /** Shown in place of the subtitle - a greyed card with no reason is a dead end. */
  disabledReason?: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'rounded-lg border p-4 text-left transition-colors',
        disabled
          ? 'cursor-not-allowed border-input opacity-55'
          : selected
            ? 'border-primary bg-primary/5'
            : 'border-input hover:bg-accent',
      )}
    >
      <p className="text-sm font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{disabled ? disabledReason : subtitle}</p>
    </button>
  );
}

export function IssueLicenseDialog({
  organizationId,
  organizationName,
  orgType,
  open,
  onOpenChange,
}: {
  organizationId: string;
  organizationName: string;
  /** Decides which plans are on offer. An individual dentist gets one. */
  orgType: OrgType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-3xl">
        <DialogBody
          organizationId={organizationId}
          organizationName={organizationName}
          orgType={orgType}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  );
}

function DialogBody({
  organizationId,
  organizationName,
  orgType,
  onOpenChange,
}: {
  organizationId: string;
  organizationName: string;
  orgType: OrgType;
  onOpenChange: (open: boolean) => void;
}) {
  const issueLicense = useIssueLicense(organizationId);
  const emailCredentials = useEmailSeatCredentials(organizationId);

  const [step, setStep] = useState<Step>(1);
  const [attempted, setAttempted] = useState<Partial<Record<Step, boolean>>>({});
  const [result, setResult] = useState<IssueLicenseResponse | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [emailed, setEmailed] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: buildInitialValues(orgType),
    validationSchema: schema,
    onSubmit: (values, helpers) => {
      issueLicense.mutate(
        {
          plan: values.plan,
          seatCount: values.seatCount,
          seats: values.seats.map((seat) => ({
            email: seat.email.trim(),
            fullName: seat.fullName.trim(),
            role: seat.role,
            validityDays: validityDaysFor(values.termMonths),
          })),
          ...(values.salesDealReference.trim()
            ? { salesDealReference: values.salesDealReference.trim() }
            : {}),
        },
        {
          onSuccess: (created) => {
            setResult(created);
          },
          onError: (error) => {
            if (error instanceof ApiError && error.fieldErrors) {
              const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), values);
              helpers.setErrors(placed);
            }
            setAttempted((current) => ({ ...current, 1: true }));
            setStep(1);
          },
        },
      );
    },
  });

  const apiError = issueLicense.error instanceof ApiError ? issueLicense.error : null;
  const soleTrader = orgType === 'individual_dentist';
  const seatCount = formik.values.seatCount;
  const named = formik.values.seats.length;
  const full = named >= seatCount;

  /**
   * `seatInput` is the typed text, not the number.
   *
   * Committing on every keystroke would mean typing "40" passes through "4"
   * and, since the people list is capped by this, immediately looks full at
   * four. It is applied on blur and on Enter instead.
   */
  const [seatInput, setSeatInput] = useState(String(formik.values.seatCount));

  const setSeatCount = (requested: number, plan: Plan = formik.values.plan): void => {
    const clamped = Math.max(1, Math.min(maxSeatsFor(plan), Math.floor(requested)));

    void formik.setFieldValue('seatCount', clamped);
    setSeatInput(String(clamped));

    /*
     * Lowering capacity below the people already named drops the ones past
     * the end - the alternative is a form that will be refused on submit for
     * a reason the operator has to work out from a validation message.
     */
    if (clamped < formik.values.seats.length) {
      void formik.setFieldValue('seats', formik.values.seats.slice(0, clamped));
    }
  };

  const commitSeatCount = () => {
    const parsed = Number.parseInt(seatInput, 10);
    // An empty or unparseable box reverts to the last good value rather than
    // guessing at a number the operator did not finish typing.
    setSeatCount(Number.isFinite(parsed) ? parsed : formik.values.seatCount);
  };

  const changePlan = (next: Plan) => {
    void formik.setFieldValue('plan', next);
    // Single Seat is exactly one, so switching to it clamps both the capacity
    // and the roster; switching the other way leaves them alone.
    setSeatCount(next === 'single_seat' ? SINGLE_SEAT_COUNT : formik.values.seatCount, next);

    /*
     * A Single Seat licence's one person is its owner.
     *
     * There is nobody else on it to be, and the organisation's licence list
     * names an owner per licence - a single-seat row whose owner column is
     * blank is a row that cannot say who to call. Still only a default: the
     * picker is right there if this one is genuinely a technician's machine.
     */
    if (next === 'single_seat') {
      void formik.setFieldValue('seats.0.role', 'owner');
    }
  };

  const addPerson = () => {
    if (full) {
      toast.error(
        `No more seats available — this licence has ${String(seatCount)}. Raise the seat count to add another.`,
      );
      return;
    }
    // Never `owner`: there is at most one, and it is on the first row.
    void formik.setFieldValue('seats', [...formik.values.seats, emptySeat()]);
  };

  const error = (path: string): string | undefined => {
    const touched: unknown = getIn(formik.touched, path);
    const message: unknown = getIn(formik.errors, path);
    if (typeof message !== 'string') return undefined;
    return touched === true || attempted[step] ? message : undefined;
  };

  const seatsError = typeof formik.errors.seats === 'string' ? formik.errors.seats : undefined;

  const banner = (): string | null => {
    if (!apiError) return null;
    if (!apiError.fieldErrors) return apiError.message;
    const { unplaced } = splitFormErrors(toFormErrors(apiError.fieldErrors), formik.values);
    return unplaced.length > 0 ? apiError.message : null;
  };

  const next = async () => {
    const errors = await formik.validateForm();
    const fields = ['plan', 'termMonths', 'salesDealReference', 'seats'];
    const stepHasError = fields.some((field) => getIn(errors, field) !== undefined);

    if (stepHasError) {
      setAttempted((current) => ({ ...current, 1: true }));
      return;
    }
    setStep(2);
  };

  const close = () => {
    onOpenChange(false);
  };

  const copy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(label);
        setTimeout(() => {
          setCopied((current) => (current === label ? null : current));
        }, 2000);
      },
      () => {
        toast.error('Could not copy. Select the text and copy it manually.');
      },
    );
  };

  if (result) {
    return (
      <>
        <DialogHeader className="shrink-0">
          <DialogTitle>Licence issued</DialogTitle>
        </DialogHeader>

        <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
          <div
            role="alert"
            className="flex gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4"
          >
            <Info className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-500" />
            <p className="text-sm text-muted-foreground">
              Copy these before you leave - generated passwords are not stored in readable form and
              cannot be shown again.
            </p>
          </div>

          <div className="space-y-4 rounded-lg border border-border p-4">
            {result.seats.map((seat, index) => (
              <div key={seat.seatId} className="space-y-2">
                {index > 0 && <div className="border-t border-border pt-2" />}
                <p className="text-sm font-medium">
                  {seat.fullName}
                  <span className="ml-2 font-normal text-muted-foreground">
                    {roleLabel(seat.role)}
                  </span>
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-20 shrink-0 text-muted-foreground">Email</span>
                  <span className="flex-1 truncate font-mono">{seat.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-20 shrink-0 text-muted-foreground">Licence key</span>
                  <span className="flex-1 truncate font-mono">{seat.licenseKey}</span>
                </div>
                {seat.password !== undefined && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-20 shrink-0 text-muted-foreground">Password</span>
                    <span className="flex-1 truncate font-mono">{seat.password}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border pt-4">
          <Button
            type="button"
            variant="outline"
            disabled={emailCredentials.isPending}
            onClick={() => {
              /*
               * One request per seat, settled together.
               *
               * allSettled rather than all: each person is a separate send, and
               * three that arrived should not be reported as a failure because
               * the fourth address bounced. The count is what gets reported.
               */
              void Promise.allSettled(
                result.seats.map((seat) =>
                  emailCredentials.mutateAsync({
                    seatId: seat.seatId,
                    ...(seat.password !== undefined ? { password: seat.password } : {}),
                  }),
                ),
              ).then((outcomes) => {
                const sent = outcomes.filter((o) => o.status === 'fulfilled').length;
                if (sent === outcomes.length) {
                  setEmailed(true);
                  toast.success(
                    sent === 1
                      ? `Sent to ${result.seats[0]?.email ?? 'the seat holder'}.`
                      : `Sent to all ${String(sent)} people.`,
                  );
                  return;
                }
                toast.error(
                  `Sent to ${String(sent)} of ${String(outcomes.length)}. The rest did not go - copy the details instead.`,
                );
              });
            }}
          >
            {emailed ? <Check className="size-4" /> : <Mail className="size-4" />}
            {emailCredentials.isPending ? 'Sending…' : emailed ? 'Emailed' : 'Email to each'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              copy(credentialsText(result.seats), 'all');
            }}
          >
            {copied === 'all' ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied === 'all' ? 'Copied' : 'Copy all'}
          </Button>
          <Button
            type="button"
            onClick={() => {
              // useIssueLicense already invalidated on success - the table and
              // this org's detail are refreshing behind this dialog right now,
              // which is safe precisely because the credentials above render
              // from local state, not the query cache.
              close();
            }}
          >
            Done
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <DialogHeader className="shrink-0">
        <DialogTitle>New licence</DialogTitle>
        <DialogDescription>
          For {organizationName} · sales-issued, no payment taken here
        </DialogDescription>
      </DialogHeader>

      <div className="shrink-0">
        <StepIndicator step={step} />
      </div>

      <FormikProvider value={formik}>
        {/* See CreateOrganizationDialog: `-mx-1 px-1` keeps a focused field's
            3px ring from being clipped by this scroll container. */}
        <div className="-mx-1 min-h-0 flex-1 space-y-5 overflow-y-auto px-1">
          {banner() && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {banner()}
            </p>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <Label>Licence mode</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {PLANS.map((option) => (
                    <PlanCard
                      key={option.value}
                      title={option.label}
                      subtitle={option.hint}
                      selected={formik.values.plan === option.value}
                      // An individual dentist is one person at one
                      // workstation, so a multi-seat plan contradicts the org
                      // type. The server refuses it too - this only saves the
                      // operator from finding out by being refused.
                      disabled={soleTrader && option.value !== 'single_seat'}
                      disabledReason="Labs only — this customer is an individual dentist."
                      onSelect={() => {
                        changePlan(option.value);
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="seat-count">Seats</Label>
                  <Input
                    id="seat-count"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={maxSeatsFor(formik.values.plan)}
                    disabled={formik.values.plan === 'single_seat'}
                    value={seatInput}
                    onChange={(event) => {
                      setSeatInput(event.target.value);
                    }}
                    onBlur={commitSeatCount}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        commitSeatCount();
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    {formik.values.plan === 'single_seat'
                      ? 'A Single Seat licence is one seat.'
                      : 'Seats bought. Name one now, the rest later.'}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="term">Licence term (months)</Label>
                  <Input
                    id="term"
                    name="termMonths"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={MAX_TERM_MONTHS}
                    value={formik.values.termMonths}
                    onChange={(event) => {
                      void formik.setFieldValue('termMonths', Number(event.target.value));
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.termMonths && formik.errors.termMonths && (
                    <p className="text-xs text-destructive">{formik.errors.termMonths}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="deal">Deal reference</Label>
                  <Input
                    id="deal"
                    placeholder="Optional"
                    {...formik.getFieldProps('salesDealReference')}
                  />
                </div>
              </div>

              <div className="space-y-1.5 rounded-lg border border-border bg-muted/40 p-3.5">
                <p className="text-xs font-medium text-foreground">
                  Pulled from <span className="font-mono">license_policies</span>
                </p>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Fingerprint policy</span>
                  <span className="font-mono font-medium">
                    {POLICY_INFO[formik.values.plan].fingerprint}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Grace period after mismatch</span>
                  <span className="font-mono font-medium">
                    {POLICY_INFO[formik.values.plan].grace}
                  </span>
                </div>
              </div>

              <FieldArray name="seats">
                {(seats) => (
                  <div className="space-y-3 border-t border-border pt-4">
                    <div className="flex items-center justify-between">
                      <Label>
                        People
                        <span className="ml-2 font-normal text-muted-foreground">
                          {named} of {seatCount} named
                        </span>
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        // Disabled AND guarded: the disabled state is the
                        // visible answer, and addPerson explains itself for
                        // anyone who reaches it another way.
                        disabled={full}
                        onClick={addPerson}
                      >
                        <Plus className="size-4" />
                        Add person
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      {full
                        ? `All ${String(seatCount)} seats are named. Raise the seat count to add another.`
                        : `${String(seatCount - named)} seat${seatCount - named === 1 ? '' : 's'} left — fill them in later from this organisation's page.`}{' '}
                      Each person gets a licence key and a portal login; their password is generated
                      and shown once, on the next screen.
                    </p>

                    {seatsError && (
                      <p role="alert" className="text-sm text-destructive">
                        {seatsError}
                      </p>
                    )}

                    {/*
                      One row per person: name, email, role, remove. Email is
                      given the widest column because it is the only one of the
                      three whose value cannot be guessed from a truncation.
                    */}
                    {formik.values.seats.map((seat, index) => (
                      <div
                        key={index}
                        className="grid gap-3 sm:grid-cols-[1fr_1.4fr_auto_auto] sm:items-start"
                      >
                        <div className="space-y-1.5">
                          <Label htmlFor={`seat-name-${String(index)}`}>Full name</Label>
                          <Input
                            id={`seat-name-${String(index)}`}
                            {...formik.getFieldProps(`seats.${String(index)}.fullName`)}
                            aria-invalid={Boolean(error(`seats.${String(index)}.fullName`))}
                          />
                          {error(`seats.${String(index)}.fullName`) && (
                            <p className="text-sm text-destructive">
                              {error(`seats.${String(index)}.fullName`)}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`seat-email-${String(index)}`}>Email</Label>
                          <Input
                            id={`seat-email-${String(index)}`}
                            type="email"
                            {...formik.getFieldProps(`seats.${String(index)}.email`)}
                            aria-invalid={Boolean(error(`seats.${String(index)}.email`))}
                          />
                          {error(`seats.${String(index)}.email`) && (
                            <p className="text-sm text-destructive">
                              {error(`seats.${String(index)}.email`)}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={`seat-role-${String(index)}`}>Role</Label>
                          <Select
                            value={seat.role}
                            onValueChange={(value) => {
                              void formik.setFieldValue(`seats.${String(index)}.role`, value);
                            }}
                          >
                            <SelectTrigger
                              id={`seat-role-${String(index)}`}
                              className="w-full sm:w-36"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLES.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Pushed down by the height of a label, so it lines up
                            with the inputs beside it rather than their labels. */}
                        <div className="sm:pt-6.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={formik.values.seats.length === 1}
                            onClick={() => {
                              // Only un-names the person; the seat they were
                              // going to hold stays on the licence, to be
                              // claimed by somebody else later.
                              seats.remove(index);
                            }}
                            aria-label={`Remove person ${String(index + 1)}`}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FieldArray>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="overflow-hidden rounded-lg border border-border">
                <p className="border-b border-border bg-muted/40 px-4 py-2.5 text-sm font-medium">
                  Review
                </p>
                <dl className="divide-y divide-border">
                  <div className="flex items-baseline justify-between gap-4 px-4 py-2 text-sm">
                    <dt className="text-muted-foreground">Organization</dt>
                    <dd className="truncate text-right font-medium">{organizationName}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 px-4 py-2 text-sm">
                    <dt className="text-muted-foreground">Plan</dt>
                    <dd className="truncate text-right font-medium">
                      {PLANS.find((p) => p.value === formik.values.plan)?.label}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 px-4 py-2 text-sm">
                    <dt className="text-muted-foreground">Seats</dt>
                    <dd className="truncate text-right font-medium">
                      {seatCount}
                      {named < seatCount && (
                        <span className="font-normal text-muted-foreground">
                          {' '}
                          · {named} named, {seatCount - named} to fill later
                        </span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 px-4 py-2 text-sm">
                    <dt className="text-muted-foreground">Term</dt>
                    <dd className="truncate text-right font-medium">
                      {formik.values.termMonths} month{formik.values.termMonths === 1 ? '' : 's'}
                    </dd>
                  </div>
                  {formik.values.salesDealReference && (
                    <div className="flex items-baseline justify-between gap-4 px-4 py-2 text-sm">
                      <dt className="text-muted-foreground">Deal reference</dt>
                      <dd className="truncate text-right font-medium">
                        {formik.values.salesDealReference}
                      </dd>
                    </div>
                  )}
                  {formik.values.seats.map((seat, index) => (
                    <div
                      key={index}
                      className="flex items-baseline justify-between gap-4 px-4 py-2 text-sm"
                    >
                      <dt className="text-muted-foreground">Person {index + 1}</dt>
                      <dd className="truncate text-right font-medium">
                        {seat.fullName || '—'} · {seat.email} ·{' '}
                        <span className="font-normal text-muted-foreground">
                          {ROLES.find((r) => r.value === seat.role)?.label}
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="flex gap-2.5 rounded-lg border border-border bg-muted/40 p-3.5">
                <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Issuing creates{' '}
                  <span className="font-mono font-medium text-foreground">
                    {formik.values.seats.length}
                  </span>{' '}
                  <span className="font-mono">license_seats</span> row
                  {formik.values.seats.length === 1 ? '' : 's'} and a portal login for each person.
                  Licence keys - and any generated password - are shown once, on the next screen.
                </p>
              </div>
            </div>
          )}
        </div>
      </FormikProvider>

      <div className="flex shrink-0 items-center justify-between border-t border-border pt-4">
        <span className="text-sm text-muted-foreground">Step {step} of 2</span>

        <div className="flex gap-2">
          {step === 1 ? (
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStep(1);
              }}
            >
              Back
            </Button>
          )}

          {step === 1 ? (
            <Button
              type="button"
              onClick={() => {
                void next();
              }}
            >
              Continue
            </Button>
          ) : (
            <Button
              type="button"
              disabled={issueLicense.isPending}
              onClick={() => {
                void formik.submitForm();
              }}
            >
              {issueLicense.isPending ? 'Issuing…' : 'Issue licence'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
